import { useQuery, useQueryClient } from "@tanstack/react-query";
import type {
  ScoreWeights,
  SessionUser,
} from "@resume-cloudflare/shared";
import { api, ApiError } from "./api";
import { useMutationToast } from "../hooks/useMutationToast";

export interface UserPrefs {
  primary_resume_id: string | null;
  score_weights: ScoreWeights | null;
}

export type MeResponse = SessionUser & { prefs: UserPrefs };

export function useMe() {
  return useQuery({
    queryKey: ["me"],
    queryFn: () => api<MeResponse>("/api/me"),
    staleTime: 30_000,
  });
}

export interface PatchMeInput {
  primary_resume_id?: string | null;
  score_weights?: ScoreWeights;
}

export interface PatchMeResponse {
  ok: true;
  prefs: UserPrefs;
}

export function usePatchMe(
  successText: string | ((data: PatchMeResponse) => string) = "Saved.",
) {
  const qc = useQueryClient();
  return useMutationToast<PatchMeResponse, MeError, PatchMeInput>({
    mutationFn: async (input) => {
      try {
        return await api<PatchMeResponse>("/api/me", {
          method: "PATCH",
          body: JSON.stringify(input),
        });
      } catch (err) {
        throw toMeError(err);
      }
    },
    onSuccess: (data) => {
      qc.setQueryData<MeResponse | undefined>(["me"], (prev) =>
        prev ? { ...prev, prefs: data.prefs } : prev,
      );
      // The shortlist query is keyed off the user's primary resume + weights
      // (server-side composite). Drop the cached page so the next view fetches
      // a freshly recomposed list.
      qc.invalidateQueries({ queryKey: ["shortlist"] });
      qc.invalidateQueries({ queryKey: ["jobs"] });
    },
    success: successText,
    errorPrefix: "Save failed",
  });
}

export interface RescoreResponse {
  enqueued: number;
  note?: string;
}

export function useRescoreAll() {
  return useMutationToast<RescoreResponse, MeError, void>({
    mutationFn: async () => {
      try {
        return await api<RescoreResponse>("/api/me/rescore", {
          method: "POST",
        });
      } catch (err) {
        throw toMeError(err);
      }
    },
    success: (data) =>
      data.enqueued === 0
        ? "All active jobs are already scored against this resume."
        : `Enqueued ${data.enqueued.toLocaleString()} jobs for scoring.`,
    errorPrefix: "Rescore failed",
  });
}

export interface MeError {
  status: number;
  error: string;
}

function toMeError(err: unknown): MeError {
  if (err instanceof ApiError) {
    let payload: { error?: string } = {};
    try {
      payload = JSON.parse(err.message);
    } catch {
      // not JSON
    }
    return {
      status: err.status,
      error: payload.error ?? err.message ?? "request_failed",
    };
  }
  return { status: 0, error: "network_error" };
}
